import ReactECharts from 'echarts-for-react';
import type { StyleConfig, DateGranularity } from '../../../../types/bi';
import { useEChartsTheme } from '../../hooks/useEChartsTheme';
import { formatCategories } from '../../utils/dateFormat';
import { buildChartClickHandler } from '../../utils/drilldown';
import type { DrilldownEvent } from '../../utils/drilldown';

interface AreaChartProps {
  data: { columns: string[]; rows: Record<string, unknown>[] };
  dimensions: string[];
  measures: { alias: string }[];
  style?: StyleConfig;
  stacked?: boolean;
  smooth?: boolean;
  dateGranularity?: DateGranularity;
  onDrillDown?: (event: DrilldownEvent) => void;
}

export default function AreaChart({
  data,
  dimensions,
  measures,
  style,
  stacked = true,
  smooth = true,
  dateGranularity,
  onDrillDown,
}: AreaChartProps) {
  const theme = useEChartsTheme();

  const dimKey = dimensions[0] || data.columns[0];
  const categories = formatCategories(
    data.rows.map((r) => String(r[dimKey] ?? '')),
    dateGranularity,
  );

  const showLabels = style?.show_data_labels ?? false;
  const labelPos = style?.label_position || 'top';
  const showGrid = style?.show_grid_lines ?? true;

  const series = measures.map((m, i) => ({
    name: m.alias,
    type: 'line' as const,
    data: data.rows.map((r) => Number(r[m.alias]) || 0),
    smooth,
    stack: stacked ? 'total' : undefined,
    areaStyle: { opacity: 0.4 },
    emphasis: { focus: 'series' as const },
    itemStyle: style?.colours?.[i] ? { color: style.colours[i] } : undefined,
    label: { show: showLabels, position: labelPos, formatter: '{c}' },
  }));

  const legendPos = style?.legend_position;
  const legendOrient = legendPos === 'left' || legendPos === 'right' ? 'vertical' : 'horizontal';
  const legendBase: Record<string, unknown> =
    legendPos === 'none' ? { show: false } : { show: measures.length > 1, orient: legendOrient };
  if (legendPos === 'top') legendBase.top = 0;
  else if (legendPos === 'left') {
    legendBase.left = 0;
    legendBase.top = 'center';
  } else if (legendPos === 'right') {
    legendBase.right = 0;
    legendBase.top = 'center';
  } else legendBase.bottom = 0;

  const option: Record<string, unknown> = {
    tooltip:
      style?.tooltip_trigger === 'none'
        ? { show: false }
        : { trigger: (style?.tooltip_trigger || 'axis') as string },
    legend: legendBase,
    grid: {
      left: '3%',
      right: '4%',
      bottom: style?.enable_data_zoom ? '15%' : '3%',
      containLabel: true,
    },
    xAxis: { type: 'category' as const, data: categories, name: style?.axis_labels?.x },
    yAxis: { type: 'value' as const, name: style?.axis_labels?.y, splitLine: { show: showGrid } },
    series,
  };

  if (style?.enable_data_zoom) {
    option.dataZoom = [
      { type: 'slider', show: true, start: 0, end: 100 },
      { type: 'inside', start: 0, end: 100 },
    ];
  }

  const events = buildChartClickHandler(dimensions, onDrillDown);

  return (
    <ReactECharts
      option={option}
      theme={theme}
      style={{ height: '100%', width: '100%' }}
      onEvents={events}
    />
  );
}
